import z from 'zod';
// import type { ApiResponse } from '@shared/types/ApiResponse';
import type { 
    ApiResponse
} from '@bonusmap/api-types';

// ===== SCHEMI BASE =====
export const apiResponseSchema = z.object({
    success: z.boolean(),
    data: z.any().optional(),
    message: z.string().optional(),
    error: z.string().optional(),
});

// ===== TIPI BASE =====
// export type ApiResponse<T = any> = {
//     success: boolean;
//     data?: T;
//     message?: string;
//     error?: string | null;
// };

// ===== FUNZIONI HELPER =====
/**
 * Funzione per creare una risposta di errore conforme a ApiResponse.
 * @param errorMessage 
 * @returns ApiResponse
 */
export const apiError = (errorMessage: string = ''): ApiResponse => {
    return {
        success: false,
        error: errorMessage,
        data: null,
    } as ApiResponse;
};

/**
 * Funzione per creare una risposta di successo conforme a ApiResponse.
 * @param data 
 * @param message 
 * @returns 
 */
export const apiSuccess = (data: any = null, message: string = ''): ApiResponse => {
    return {
        success: true,
        data: data,
        message: message,   
    } as ApiResponse;
};

// ===== SCHEMI UTENTE =====
export const userSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  // Altri campi utente da aggiungere secondo necessità
});

// ===== SCHEMI LOGIN CLASSICO =====
export const loginInputSchema = z.object({
  email: z.string().email().describe('Email dell\'utente'),
  password: z.string().min(1).describe('Password dell\'utente')
});

// ===== SCHEMI TOTP - SETUP =====


export const totpSetupInputSchema = z.object({
  // Il setupToken viene passato come header Authorization
});

export const totpVerifyInputSchema = z.object({
  setupToken: z.string().describe('Token di setup ricevuto durante il login'),
  code: z.string().length(6).describe('Codice TOTP a 6 cifre generato dall\'app')
});

export const totpVerifyResponseSchema = z.object({
  success: z.boolean().describe('Indica se la verifica è andata a buon fine'),
  message: z.string().describe('Messaggio di conferma o errore'),
  token: z.string().optional().describe('JWT finale (solo se setup completato con successo)'),
  user: userSchema.optional().describe('Dati utente (solo se setup completato)'),
  cookieName: z.string().optional().describe('Nome del cookie JWT')
});

// ===== SCHEMI LOGIN CON TOTP (SENZA DISCRIMINATED UNION) =====
export const loginRequiresTotpSetupSchema = z.object({
  requiresTotpSetup: z.literal(true),
  setupToken: z.string().describe('Token temporaneo per procedere con il setup TOTP'),
  message: z.string().optional().describe('Messaggio che spiega la necessità del setup')
});

export const loginRequiresTotpCodeSchema = z.object({
  requiresTotpCode: z.literal(true),
  tempToken: z.string().describe('Token temporaneo per completare il login con TOTP'),
  message: z.string().optional().describe('Messaggio che richiede il codice TOTP')
});

export const loginCompletedSchema = z.object({
  token: z.string().describe('JWT finale'),
  user: userSchema.describe('Dati dell\'utente autenticato'),
  cookieName: z.string().describe('Nome del cookie JWT')
});

// Schema flessibile per la risposta login - accetta qualsiasi delle tre forme
export const loginResponseDataSchema = z.union([
  loginCompletedSchema,
  loginRequiresTotpSetupSchema,
  loginRequiresTotpCodeSchema
]);

// Schema per il tipo con discriminator (usato solo internamente nel client)
// export const loginResponseDataWithTypeSchema = z.discriminatedUnion('type', [
//   z.object({
//     type: z.literal('completed'),
//     ...loginCompletedSchema.shape
//   }),
//   z.object({
//     type: z.literal('requiresSetup'),
//     ...loginRequiresTotpSetupSchema.shape
//   }),
//   z.object({
//     type: z.literal('requiresCode'),
//     ...loginRequiresTotpCodeSchema.shape
//   })
// ]);

export const loginWithTotpInputSchema = z.object({
  tempToken: z.string().describe('Token temporaneo ricevuto durante il login'),
  totpCode: z.string().min(6).max(8).describe('Codice TOTP (6 cifre) o backup code (8 caratteri)')
});

export const loginWithTotpResponseSchema = z.object({
  token: z.string().describe('JWT finale'),
  user: userSchema.describe('Dati dell\'utente autenticato'),
  cookieName: z.string().describe('Nome del cookie JWT'),
  message: z.string().optional().describe('Messaggio di conferma')
});

// ===== SCHEMI BACKUP CODES =====
export const regenerateBackupCodesInputSchema = z.object({
  password: z.string().min(1).describe('Password dell\'utente per conferma identità')
});

export const regenerateBackupCodesResponseSchema = z.object({
  backup_codes: z.array(z.string()).describe('Nuovi codici backup generati'),
  message: z.string().describe('Messaggio di conferma')
});

// ===== HELPER SCHEMAS =====
export const totpCodeSchema = z.string().regex(/^\d{6}$/, 'Il codice TOTP deve essere di 6 cifre');

export const backupCodeSchema = z.string().regex(/^[A-Z0-9]{8}$/, 'Il codice backup deve essere di 8 caratteri alfanumerici');

export const totpOrBackupCodeSchema = z.string().refine(
  (code) => /^\d{6}$/.test(code) || /^[A-Z0-9]{8}$/.test(code),
  'Inserisci un codice TOTP (6 cifre) o un codice backup (8 caratteri)'
);

// ===== TIPI TYPESCRIPT =====
// export type User = z.infer<typeof userSchema>;
export type LoginInput = z.infer<typeof loginInputSchema>;

// Tipi TOTP Setup
export type TotpSetupInput = z.infer<typeof totpSetupInputSchema>;
export type TotpVerifyInput = z.infer<typeof totpVerifyInputSchema>;
// export type TotpVerifyResponse = z.infer<typeof totpVerifyResponseSchema>;
export type {TotpVerifyResponse} from '@bonusmap/api-types';

// Tipi Login (raw dal server)
export type LoginRequiresTotpSetup = z.infer<typeof loginRequiresTotpSetupSchema>;
export type LoginRequiresTotpCode = z.infer<typeof loginRequiresTotpCodeSchema>;
export type LoginCompleted = z.infer<typeof loginCompletedSchema>;
export type LoginResponseData = z.infer<typeof loginResponseDataSchema>;

// Tipi Login (con discriminator per il client)
// export type LoginResponseDataWithType = z.infer<typeof loginResponseDataWithTypeSchema>;

export type LoginWithTotpInput = z.infer<typeof loginWithTotpInputSchema>;
export type LoginWithTotpResponse = z.infer<typeof loginWithTotpResponseSchema>;

// Tipi Backup Codes
export type RegenerateBackupCodesInput = z.infer<typeof regenerateBackupCodesInputSchema>;
export type RegenerateBackupCodesResponse = z.infer<typeof regenerateBackupCodesResponseSchema>;

// ===== SCHEMI TERRITORIAL SEARCH =====
export const territorialSearchParamsSchema = z.object({
  type: z.enum(['nazione', 'regione', 'provincia', 'comune']).optional().describe('Tipo di territorio da cercare'),
  name: z.string().optional().describe('Nome del territorio (ricerca parziale, case-insensitive)'),
  region_name: z.string().optional().describe('Nome della regione (ricerca parziale, case-insensitive)'),
  province_name: z.string().optional().describe('Nome della provincia (ricerca parziale, case-insensitive)'),
  limit: z.number().optional().describe('Numero massimo di risultati (default: 50, max: 1000)'),
  offset: z.number().optional().describe('Numero di risultati da saltare per paginazione (default: 0)')
});

export const territorialSearchResultSchema = z.object({
  territorial_id: z.string().nullable().describe('ID territoriale univoco'),
  territorial_code: z.string().nullable().describe('Codice territoriale'),
  name: z.string().nullable().describe('Nome del territorio'),
  type: z.string().nullable().describe('Tipo (comune, provincia, regione, nazione)'),
  level_num: z.number().nullable().describe('Livello gerarchico (1-4)'),
  parent_id: z.string().nullable().describe('ID del territorio padre'),
  sigla: z.string().nullable().describe('Sigla territoriale'),
  region_id: z.string().nullable().describe('ID della regione'),
  region_name: z.string().nullable().describe('Nome della regione'),
  province_id: z.string().nullable().describe('ID della provincia'),
  province_name: z.string().nullable().describe('Nome della provincia'),
  province_sigla: z.string().nullable().describe('Sigla della provincia'),
  flag_capoluogo: z.boolean().nullable().describe('Indica se è capoluogo'),
  codice_belfiore: z.string().nullable().describe('Codice Belfiore'),
  lat: z.number().nullable().describe('Latitudine'),
  lon: z.number().nullable().describe('Longitudine')
});

export const territorialAutocompleteParamsSchema = z.object({
  q: z.string().describe('Query di ricerca per nome territorio'),
  limit: z.number().optional().describe('Numero massimo di risultati (default: 10)')
});

export const territorialAutocompleteItemSchema = z.object({
  id: z.string().nullable().describe('ID territoriale'),
  label: z.string().describe('Label formattata per display (es: "Milano (Milano, Lombardia)")'),
  value: z.string().nullable().describe('Valore da utilizzare per selezione')
});

// ===== TIPI TERRITORIAL SEARCH =====
export type TerritorialSearchParams = z.infer<typeof territorialSearchParamsSchema>;
export type TerritorialSearchResult = z.infer<typeof territorialSearchResultSchema>;
export type TerritorialAutocompleteParams = z.infer<typeof territorialAutocompleteParamsSchema>;
export type TerritorialAutocompleteItem = z.infer<typeof territorialAutocompleteItemSchema>;